/**
 * Accueil du Curieux (`/player/dashboard`) — adulte qui refait des maths pour
 * lui. À l'ouverture il doit voir QUOI FAIRE maintenant :
 *  1. son niveau de révision (dernier bilan, sinon niveau choisi) — ou le
 *     choisir / faire son bilan s'il n'en a pas ;
 *  2. 3 exercices de ce niveau pas encore faits (« Pour toi ») ;
 *  3. ses SKILLCARDS (celles qui perdent de l'HORA en tête) ;
 *  4. son bilan de niveau (en haut tant qu'il n'en a pas fait).
 * JeJoue, le Feed et le réseau sont dans la barre de navigation : pas de
 * doublon ici. La déconnexion est dans le Profil.
 */

import { AccountSwitcher } from '@/components/admin/account-switcher';
import { DashboardTrousse } from '@/features/trousse/dashboard-trousse';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight, Clock, Gamepad2 } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { SECONDARY_SCHOOL_LEVELS, SCHOOL_LEVELS, type SchoolLevel } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { AvatarCircle } from '@/components/avatar-circle';
import { StudentSkillcardsSection } from '@/features/skillcards/student-skillcards-section';
import { SelfAssessmentCard } from '@/features/skill-diagnostic/self-assessment-card';
import { ScratchTutorialCard } from '@/features/scratch-tutorial/scratch-tutorial-card';
import { useAdultDiagnosticState } from '@/lib/skill-diagnostic';
import { skillcardStats, useStudentSkillcards } from '@/lib/cards/student-skillcards';
import { SkillcardPromoBanner } from '@/features/skillcards/skillcard-promo-banner';
import {
  distinctCompleted,
  firstName,
  readStoredPlayerLevel,
  revisionLevel,
  storePlayerLevel,
  suggestExercises,
} from '@/lib/player/overview';
import {
  completedExerciseIds,
  exerciseRouteParams,
  usePlayerCompletedAttempts,
  usePlayerExercises,
  type PlayerExerciseRow,
} from '@/lib/player/queries';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const DIFFICULTY_LABEL = { easy: 'Facile', medium: 'Moyen', hard: 'Difficile' } as const;

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
}

export function PlayerDashboardPage() {
  const { user, profile } = useAuth();
  const playerId = user?.id ?? '';

  const diag = useAdultDiagnosticState(playerId);
  const [stored, setStored] = useState<SchoolLevel | null>(() => readStoredPlayerLevel());
  const [choosing, setChoosing] = useState(false);
  const level = revisionLevel({
    stored,
    inProgressLevel: diag.data?.in_progress?.level,
    lastCompletedLevel: diag.data?.last_completed?.level,
  });
  const hasBilan = !!diag.data?.last_completed || !!diag.data?.in_progress;

  const attempts = usePlayerCompletedAttempts(playerId);
  const done = completedExerciseIds(attempts.data);
  const exercises = usePlayerExercises({ level: level ?? '', limit: 40 });
  const suggestions = suggestExercises(exercises.data ?? [], done, level, 3);

  const cards = useStudentSkillcards(playerId);
  const stats = skillcardStats(cards.data ?? []);
  const exercisesDone = distinctCompleted(
    (attempts.data ?? []).filter(
      (a): a is { exercise_id: string; completed_at: string | null } => !!a.exercise_id,
    ),
  );

  function pickLevel(next: SchoolLevel) {
    storePlayerLevel(next);
    setStored(next);
    setChoosing(false);
  }

  if (!profile || !user) return null;

  const name = firstName(profile.display_name);
  const summary = [
    exercisesDone > 0 && `${exercisesDone} exercice${exercisesDone > 1 ? 's faits' : ' fait'}`,
    stats.count > 0 && `${stats.count} SKILLCARD${stats.count > 1 ? 'S' : ''}`,
    stats.mastered > 0 && `${stats.mastered} maîtrisée${stats.mastered > 1 ? 's' : ''}`,
  ].filter(Boolean);

  return (
    <div className="pb-safe mx-auto max-w-2xl space-y-7 px-4 py-6 sm:py-8">
      {/* ── En-tête : qui, et quel niveau il révise ── */}
      <header className="flex items-center gap-3">
        <AvatarCircle name={profile.display_name} src={profile.avatar_url} size="lg" />
        <div className="min-w-0">
          <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
            {greeting()}
            {name ? `, ${name}` : ''}&nbsp;!
          </h1>
          {summary.length > 0 ? (
            <p className="text-ink-500 flex flex-wrap gap-x-1.5 font-sans text-sm">
              {summary.map((part, i) => (
                <span key={i} className="whitespace-nowrap">
                  {i > 0 && <span aria-hidden>· </span>}
                  {part}
                </span>
              ))}
            </p>
          ) : (
            <p className="text-ink-500 font-sans text-sm">Prêt à refaire des maths ?</p>
          )}
        </div>
        <div className="ml-auto flex items-start gap-2 self-start">
          <AccountSwitcher />
          <DashboardTrousse />
        </div>
      </header>
      <SkillcardPromoBanner to="/player/skillcards" ctaLabel="Voir mes SKILLCARDS" />

      {/* ── Bilan en premier tant qu'il n'en a pas fait ── */}
      {!hasBilan && playerId && <SelfAssessmentCard userId={playerId} role="player" />}
      <ScratchTutorialCard />

      {/* ── Pour toi : exercices du niveau révisé ── */}
      <section aria-labelledby="for-you-title" className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
          <div className="min-w-0">
            <h2 id="for-you-title" className="font-display text-ink-900 text-lg font-bold">
              Pour toi
            </h2>
            {level && !choosing && (
              <p className="text-ink-500 font-sans text-sm">
                Tu révises le niveau <strong className="text-ink-800">{LEVEL_LABEL[level]}</strong>
                {' · '}
                <button
                  type="button"
                  onClick={() => setChoosing(true)}
                  className="text-primary-600 -my-3 inline-flex min-h-[2.75rem] items-center font-semibold"
                >
                  changer
                </button>
              </p>
            )}
          </div>
          <Link
            to="/player/jouer"
            className="text-primary-600 flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
          >
            Tout JeJoue <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>

        {(!level || choosing) && (
          <LevelChooser
            current={level}
            onPick={pickLevel}
            onCancel={level ? () => setChoosing(false) : undefined}
          />
        )}

        {level && !choosing && (
          <>
            {exercises.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-ink-100 h-[4.5rem] animate-pulse rounded-2xl" />
                ))}
              </div>
            ) : suggestions.length > 0 ? (
              <ul className="space-y-2">
                {suggestions.map((ex) => (
                  <li key={ex.id}>
                    <SuggestionRow ex={ex} />
                  </li>
                ))}
              </ul>
            ) : (
              <Link
                to="/player/jouer"
                className="border-ink-200 ocean:bg-white/5 flex items-center gap-3 rounded-2xl border border-dashed bg-white p-4 active:scale-[0.98]"
              >
                <Gamepad2 className="text-primary-500 h-5 w-5 shrink-0" aria-hidden />
                <span className="text-ink-700 text-sm">
                  Tu as fait tous les exercices récents de ce niveau. Explore JeJoue pour en trouver
                  d’autres.
                </span>
              </Link>
            )}
          </>
        )}
      </section>

      {/* ── SKILLCARDS ── */}
      <StudentSkillcardsSection
        studentId={playerId}
        allHref="/player/skillcards"
        empty="Chaque exercice travaille une ou plusieurs SKILLCARDS (méthodes, théorèmes, formules). Elles s’ajoutent ici dès ton premier exercice : réponds juste pour faire monter leur maîtrise."
      />

      {/* ── Bilan déjà fait : rappel discret, en bas ── */}
      {hasBilan && playerId && <SelfAssessmentCard userId={playerId} role="player" />}
    </div>
  );
}

function LevelChooser({
  current,
  onPick,
  onCancel,
}: {
  current: SchoolLevel | null;
  onPick: (l: SchoolLevel) => void;
  onCancel?: () => void;
}) {
  return (
    <div className="border-ink-100 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4">
      <p className="text-ink-800 text-sm font-semibold">Quel niveau veux-tu réviser ?</p>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
        {SECONDARY_SCHOOL_LEVELS.map((l) => (
          <button
            key={l.value}
            type="button"
            aria-pressed={current === l.value}
            onClick={() => onPick(l.value)}
            className={cn(
              'min-h-[2.75rem] rounded-xl border-2 px-2 text-sm font-semibold active:scale-[0.98]',
              current === l.value
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-700 ocean:bg-white/5 bg-white',
            )}
          >
            {l.label}
          </button>
        ))}
      </div>
      <p className="text-ink-500 text-xs">Pas sûr ? Le bilan de niveau (15 questions) te le dit.</p>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-ink-500 min-h-[2.75rem] text-sm font-semibold"
        >
          Annuler
        </button>
      )}
    </div>
  );
}

function SuggestionRow({ ex }: { ex: PlayerExerciseRow }) {
  const params = exerciseRouteParams(ex);
  if (!params) return null;
  return (
    <Link
      to="/learn/$subject/$level/$chapter/$exercise"
      params={params}
      className="border-ink-100 ocean:bg-white/5 hover:border-primary-200 flex min-h-[2.75rem] items-center gap-3 rounded-2xl border bg-white p-3 transition-colors active:scale-[0.98]"
    >
      <span className="bg-primary-50 ocean:bg-primary-500/15 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
        <Gamepad2 className="text-primary-600 h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 block truncate text-sm font-bold">
          <MathText>{ex.title}</MathText>
        </span>
        <span className="text-ink-500 flex items-center gap-2 truncate text-xs">
          <span className="truncate">{ex.chapters?.title}</span>
          <span aria-hidden>·</span>
          <span>{DIFFICULTY_LABEL[ex.difficulty]}</span>
          {ex.estimated_minutes ? (
            <span className="flex shrink-0 items-center gap-0.5">
              <Clock className="h-3 w-3" aria-hidden /> {ex.estimated_minutes} min
            </span>
          ) : null}
        </span>
      </span>
      <ChevronRight className="text-ink-400 h-4 w-4 shrink-0" aria-hidden />
    </Link>
  );
}
